import { PlayerImage } from "@/components/PlayerImage";
import { AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { pointsToYen } from "@/utils/pointsToYen";
import { CheckIcon, ThumbaTrophyIcon } from "@roman/shared/components/Icons";
import { AvatarIcon } from "@roman/shared/components/Icons/AvatarIcon";
import type { EntryResult } from "@roman/shared/types/Result";
import { cn, getColorStyleByRank } from "@roman/shared/utils";
import { ChevronDownIcon } from "lucide-react";

interface EntryRowProps {
  entry: EntryResult;
  isYourEntry: boolean;
  onSelectEntry: (entry: EntryResult | null) => void;
  index: number;
  setExpandedValues: React.Dispatch<React.SetStateAction<string[]>>;
  rankWidth: string;
  playerImageMap: Record<string, string | null>;
}

export const EntryRow = ({
  entry,
  isYourEntry,
  onSelectEntry,
  index,
  setExpandedValues,
  rankWidth,
  playerImageMap,
}: EntryRowProps) => {
  const toggleExpandedValue = (event: React.MouseEvent, value: string) => {
    event.stopPropagation();

    setExpandedValues((prev: string[]) => {
      const index = prev.indexOf(value);
      if (index === -1) {
        return [...prev, value];
      }
      return prev.filter((v: string) => v !== value);
    });
  };

  const isCaptain = (playerId: string): boolean => {
    return entry.selectedCaptains.captainPlayerId === playerId;
  };

  const isViceCaptain = (playerId: string): boolean => {
    if (entry.selectedCaptains.type === "0002") {
      return entry.selectedCaptains.viceCaptainPlayerId === playerId;
    }
    return false;
  };

  return (
    <AccordionItem
      value={`entry-${index}`}
      className="w-full text-icon px-2 py-3 data-[state=open]:bg-[#F3F8FF] first:border-t first:border-light-gray"
    >
      <AccordionTrigger
        className="flex items-center space-x-2 hover:no-underline w-full cursor-pointer pointer-events-none h-[45px]"
        iconClassName="w-0 hidden"
        onClick={() => onSelectEntry(entry)}
      >
        <div
          className={cn(
            "flex items-center pointer-events-auto",
            entry.rank >= 1000 ? "gap-3" : entry.rank >= 100 ? "gap-2" : "gap-1",
          )}
        >
          <p className={cn("text-center text-2xl", rankWidth, getColorStyleByRank(entry.rank))}>{entry.rank}</p>
          {entry.userPhotoUrl ? (
            <div className="w-[36px] h-[36px]">
              <img
                src={entry.userPhotoUrl || "/assets/player-shirt.png"}
                alt={entry.userName.substring(0, 1)}
                className=" rounded-full"
                referrerPolicy="no-referrer"
              />
            </div>
          ) : (
            <div className="content-center justify-items-center rounded-full w-[36px] h-[36px] bg-secondary">
              <AvatarIcon />
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2 w-full pointer-events-auto">
          <div className="flex items-center gap-2 ">
            {isYourEntry && (
              <div
                id="player-my-team"
                className={
                  "rounded-full p-1 w-[18px] h-[18px] flex items-center justify-center cursor-pointer bg-secondary"
                }
              >
                <CheckIcon className="text-white" />
              </div>
            )}
            <p className="text-xs sm:text-sm">{entry.userName}</p>
          </div>
        </div>

        <div className="flex justify-end items-center space-x-1 md:space-x-10 transition-all cursor-default w-48">
          <span className="font-semibold text-right">{entry.fantasyPoint.toFixed(2)}pt</span>

          <div className="flex items-center space-x-1 justify-end whitespace-nowrap">
            <ThumbaTrophyIcon className="w-[25px] h-[25px]" />
            <span className="font-semibold">
              {pointsToYen(entry.prize)}
              <span>円</span>
            </span>
          </div>
        </div>

        <ChevronDownIcon
          strokeWidth={2}
          className="text-[#ADADAD] min-w-[26px] cursor-pointer pointer-events-auto transition-transform duration-200"
          onClick={(event) => toggleExpandedValue(event, `entry-${index}`)}
        />
      </AccordionTrigger>

      <AccordionContent className="overflow-hidden pb-0">
        <div className="ml-[5rem] overflow-x-scroll hide-scrollbar">
          <div className="flex space-x-2 px-2 pt-1 w-max">
            {entry.selectedPlayers.map((player) => (
              <PlayerImage
                key={player}
                url={playerImageMap[player] || "/assets/player-shirt.png"}
                name={`player-${player}`}
                isCaptain={isCaptain(player)}
                isViceCaptain={isViceCaptain(player)}
                badgeClassName="w-[20px] h-[10px]"
              />
            ))}
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};
